import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { CloseIcon } from "@/assets/icons";
import { FlexColumn } from "@/ui/FlexContainer/FlexContainer";
import { useTranslation } from "react-i18next";
import { useUserDataContext } from "@/contexts/UserContext";
import { useAuthStore } from "@/store/auth";
import { useNotificationsDataContext } from "@/contexts/NotificationsContext";
import { useLocation } from "react-router-dom";
import { authPath } from "@/utils/authRedirect";
import cls from "./styles.module.scss";

interface MenuProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
}

export const Menu: React.FC<MenuProps> = ({ isMenuOpen, setIsMenuOpen }) => {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const { userData } = useUserDataContext();
  const { unreadCount } = useNotificationsDataContext();
  const location = useLocation();

  const menuRef = useRef<HTMLDivElement>(null);
  const [isPetsOpen, setIsPetsOpen] = useState(false);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        handleCloseMenu();
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [handleCloseMenu]);

  const pets = userData?.pets ?? [];
  const hasMultiplePets = pets.length > 1;
  const isAuthPage = [
    "/login",
    "/register",
    "/reset-password",
    "/set-new-password",
    "/otp",
    "/logout",
  ].includes(location.pathname);
  const returnPath = isAuthPage
    ? null
    : `${location.pathname}${location.search}${location.hash}`;

  const singlePetLink = useMemo(() => {
    if (pets.length === 1) return `/pets/${pets[0].id}`;
    if (userData) return "/pets/create";
    return "/login";
  }, [pets, userData]);

  return (
    <div
      className={cls.menuBlock}
      ref={menuRef}
      style={{
        transform: !isMenuOpen ? "translateX(100%)" : "translateX(0)",
      }}
    >
      <div className={cls.close} onClick={() => setIsMenuOpen(!isMenuOpen)}>
        <CloseIcon />
      </div>
      <FlexColumn justifyContent="center" className={cls.menu}>
        <FlexColumn gap={2}>
          {!isAuthenticated && (
            <div className={cls.authLinks}>
              <a
                href={authPath("/login", returnPath)}
                className={cls.authPrimary}
                onClick={handleCloseMenu}
              >
                {t("common.authorization")}
              </a>
              <a
                href={authPath("/register", returnPath)}
                className={cls.authSecondary}
                onClick={handleCloseMenu}
              >
                {t("common.registration")}
              </a>
            </div>
          )}

          {!hasMultiplePets && (
            <a href={singlePetLink} onClick={handleCloseMenu}>
              {t("common.myPet")}
            </a>
          )}

          {hasMultiplePets && (
            <div className={cls.petsBlock}>
              <button
                type="button"
                className={cls.petsHeader}
                onClick={() => setIsPetsOpen((prev) => !prev)}
                aria-expanded={isPetsOpen}
              >
                <span>{t("common.myPet")}</span>
                <span
                  className={cls.petsArrow}
                  data-open={isPetsOpen ? "true" : "false"}
                >
                  ▾
                </span>
              </button>

              <div
                className={cls.petsListWrapper}
                data-open={isPetsOpen ? "true" : "false"}
              >
                <div className={cls.petsList}>
                  {pets.map((pet) => (
                    <a
                      key={pet.id}
                      href={`/pets/${pet.id}`}
                      onClick={handleCloseMenu}
                    >
                      {pet.name || t("common.myPet")}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          <a
            href="/profile"
            onClick={handleCloseMenu}
            className={cls.notificationLink}
          >
            <span>{t("common.profile")}</span>
            {isAuthenticated && unreadCount > 0 && (
              <span className={cls.notificationCount}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </a>
          <a href="/sky" onClick={handleCloseMenu}>
            {t("common.sky")}
          </a>
          <a href="/about-us" onClick={handleCloseMenu}>
            {t("common.aboutUs")}
          </a>
          <a href="/blog" onClick={handleCloseMenu}>
            {t("common.blog")}
          </a>
          <a href="/faq" onClick={handleCloseMenu}>
            FAQ
          </a>
          {isAuthenticated && (
            <a href="/logout" onClick={handleCloseMenu}>
              {t("common.logout")}
            </a>
          )}
        </FlexColumn>
      </FlexColumn>
    </div>
  );
};
